import React, { useState, useMemo, useEffect } from 'react';
import { View, TextInput, TouchableOpacity, StyleSheet, ScrollView, KeyboardAvoidingView, Platform, Alert, ActivityIndicator, Image, Keyboard, StatusBar } from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import * as ImagePicker from 'expo-image-picker';
import { Ionicons } from '@expo/vector-icons';
import { StackScreenProps } from '@react-navigation/stack';
import { CustomPicker } from '../../components/common/CustomPicker';
import { useAuth } from '../../context/AuthContext';
import { AuthStackParamList } from '../../types/navigation';
import ThemedText from '../../components/common/ThemedText';
import { Theme } from '../../theme/colors';
import { getCountries, getCitiesForCountry } from '../../data/countriesAndCities';
import * as NavigationBar from 'expo-navigation-bar';

type Props = StackScreenProps<AuthStackParamList, 'Onboarding'>;

// Profession options
const PROFESSIONS = [
  'Software Engineer',
  'Product Designer',
  'Product Manager',
  'Marketing Manager',
  'Sales Manager',
  'Business Analyst',
  'Data Scientist',
  'DevOps Engineer',
  'Frontend Developer',
  'Backend Developer',
  'Full Stack Developer',
  'UX Designer',
  'UI Designer',
  'Content Writer',
  'Project Manager',
  'HR Manager',
  'Finance Manager',
  'Operations Manager',
  'Consultant',
  'Entrepreneur',
  'Freelancer',
  'Other',
];

// Industry options
const INDUSTRIES = [
  'Technology',
  'Finance',
  'Healthcare',
  'Education',
  'Retail',
  'Manufacturing',
  'Real Estate',
  'Consulting',
  'Marketing',
  'Media & Entertainment',
  'Hospitality',
  'Transportation',
  'Energy',
  'Agriculture',
  'Construction',
  'Legal',
  'Government',
  'Non-Profit',
  'Other',
];

// Gender options
const GENDERS = [
  'Male',
  'Female',
  'Prefer not to say',
];

// Form field configuration
const MAX_TEXT_LENGTH = 50;

type FormField = 'name' | 'profession' | 'industry' | 'city' | 'country' | 'gender';

const ONBOARDING_COLORS = {
  background: '#000000',
  border: 'rgba(255, 255, 255, 0.2)',
  borderLight: 'rgba(255, 255, 255, 0.1)',
  text: '#FFFFFF',
  textSecondary: '#9CA3AF',
  muted: '#6B7280',
  buttonEnabled: '#C4963E',
  buttonDisabled: '#1F1F1F',
  buttonText: '#000000',
};

const ONBOARDING_PICKER_THEME_OVERRIDES: Partial<Theme> = {
  border: 'rgba(255, 255, 255, 0.2)',
  borderLight: 'rgba(255, 255, 255, 0.08)',
  backgroundSecondary: 'rgba(255, 255, 255, 0.05)',
  disabled: 'rgba(255, 255, 255, 0.08)',
  text: '#FFFFFF',
  textSecondary: '#9CA3AF',
  placeholder: '#6B7280',
  accent: '#FFFFFF',
  card: '#111111',
  backgroundTertiary: 'rgba(255, 255, 255, 0.12)',
};

const OnboardingScreen: React.FC<Props> = () => {
  const [formData, setFormData] = useState({
    name: '',
    profession: '',
    industry: '',
    city: '',
    country: '',
    gender: '',
    photo: '',
  });
  const [loading, setLoading] = useState(false);
  const [isKeyboardVisible, setKeyboardVisible] = useState(false);
  const { completeOnboarding } = useAuth();
  const insets = useSafeAreaInsets();

  useEffect(() => {
    const keyboardDidShowListener = Keyboard.addListener(
      'keyboardDidShow',
      () => setKeyboardVisible(true)
    );
    const keyboardDidHideListener = Keyboard.addListener(
      'keyboardDidHide',
      () => setKeyboardVisible(false)
    );

    if (Platform.OS === 'android') {
      NavigationBar.setButtonStyleAsync('light').catch(() => {});
    }

    return () => {
      keyboardDidShowListener.remove();
      keyboardDidHideListener.remove();
    };
  }, []);

  // Get available cities based on selected country
  const availableCities = useMemo(() => {
    if (!formData.country) {
      return ['Other'];
    }
    return getCitiesForCountry(formData.country);
  }, [formData.country]);

  const handleInputChange = (field: FormField, value: string) => {
    // Trim whitespace and enforce max length
    let processedValue = value;
    
    if (field === 'name' || field === 'city') {
      processedValue = value.slice(0, MAX_TEXT_LENGTH);
    }
    
    // If country changes, reset city
    if (field === 'country') {
      setFormData(prev => ({
        ...prev,
        country: processedValue,
        city: '', // Reset city when country changes
      }));
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: processedValue,
    }));
  };

  const handlePickImage = async () => {
    try {
      // Photo picker on Android 13+ uses system UI - no READ_MEDIA_* permissions needed

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.5,
      });

      if (!result.canceled && result.assets[0]) {
        setFormData(prev => ({
          ...prev,
          photo: result.assets[0].uri,
        }));
      }
    } catch (error) {
      console.error('Error picking image:', error);
      Alert.alert('Error', 'Failed to pick image. Please try again.');
    }
  };

  const validateForm = (): boolean => {
    // Check all fields are filled
    if (!formData.name.trim()) {
      Alert.alert('Required Field', 'Please enter your full name.');
      return false;
    }
    if (!formData.profession) {
      Alert.alert('Required Field', 'Please select your primary profession.');
      return false;
    }
    if (!formData.industry) {
      Alert.alert('Required Field', 'Please select your industry.');
      return false;
    }
    if (!formData.city.trim()) {
      Alert.alert('Required Field', 'Please enter your city.');
      return false;
    }
    if (!formData.country) {
      Alert.alert('Required Field', 'Please select your country.');
      return false;
    }
    if (!formData.gender) {
      Alert.alert('Required Field', 'Please select your gender.');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Trim whitespace from text fields
      const cleanedData = {
        name: formData.name.trim(),
        profession: formData.profession,
        industry: formData.industry,
        city: formData.city.trim(),
        country: formData.country,
        gender: formData.gender,
        photo: formData.photo || undefined,
      };
      
      await completeOnboarding(cleanedData);
      
      console.log('✅ Onboarding completed! Awaiting FindFriends flow...');
    } catch (error) {
      Alert.alert('Error', error instanceof Error ? error.message : 'Failed to complete onboarding. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Check if all fields are filled
  const isFormValid = 
    formData.name.trim() !== '' &&
    formData.profession !== '' &&
    formData.industry !== '' &&
    formData.city.trim() !== '' &&
    formData.country !== '' &&
    formData.gender !== '';
  const isButtonDisabled = !isFormValid || loading;
  const buttonBackgroundColor = isButtonDisabled ? ONBOARDING_COLORS.buttonDisabled : ONBOARDING_COLORS.buttonEnabled;
  const buttonTextColor = isButtonDisabled ? ONBOARDING_COLORS.textSecondary : ONBOARDING_COLORS.buttonText;

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <StatusBar barStyle="light-content" backgroundColor="#000000" translucent={false} />
      <View style={styles.contentWrapper}>
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          style={styles.keyboardView}
          keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 0}
        >
          <ScrollView
            style={[styles.scrollView, Platform.OS === 'web' && { height: '100vh' }]}
            contentContainerStyle={[
              styles.scrollContent,
              { paddingBottom: 24 + (insets.bottom || 0) }
            ]}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            {/* Header */}
            <View style={styles.header}>
              <ThemedText style={styles.title}>Set up your profile</ThemedText>
              <ThemedText style={styles.subtitle}>This will only take a minute</ThemedText>
            </View>

            {/* Form */}
            <View style={styles.form}>
              {/* Profile Photo */}
              <View style={styles.photoContainer}>
                <TouchableOpacity
                  style={styles.photoButton}
                  onPress={handlePickImage}
                  disabled={loading}
                >
                  {formData.photo ? (
                    <Image source={{ uri: formData.photo }} style={styles.photoImage} />
                  ) : (
                    <View style={styles.photoPlaceholder}>
                      <Ionicons name="camera" size={32} color="#6B7280" />
                      <ThemedText style={styles.photoText}>Add Photo</ThemedText>
                    </View>
                  )}
                </TouchableOpacity>
                <ThemedText style={styles.photoLabel}>Optional</ThemedText>
              </View>

              {/* Full Name */}
              <View style={styles.fieldContainer}>
                <ThemedText style={styles.label}> 
                  Full Name <ThemedText style={styles.required}>*</ThemedText>
                </ThemedText>
                <TextInput
                  style={styles.input}
                  placeholder="Enter your full name"
                  placeholderTextColor="#6B7280"
                  value={formData.name}
                  onChangeText={value => handleInputChange('name', value)}
                  maxLength={MAX_TEXT_LENGTH}
                  autoCapitalize="words"
                  editable={!loading}
                />
                <ThemedText style={styles.characterCount}> 
                  {formData.name.length}/{MAX_TEXT_LENGTH}
                </ThemedText>
              </View>

              {/* Primary Profession */}
              <View style={styles.fieldContainer}>
                <ThemedText style={styles.label}> 
                  Primary Profession <ThemedText style={styles.required}>*</ThemedText>
                </ThemedText>
                <CustomPicker
                  selectedValue={formData.profession || undefined}
                  onValueChange={value => handleInputChange('profession', value || '')}
                  items={[
                    { label: 'Select your profession', value: undefined },
                    ...PROFESSIONS.map(profession => ({
                      label: profession,
                      value: profession,
                    })),
                  ]}
                  placeholder="Select your profession"
                  searchable
                  searchPlaceholder="Search professions"
                  themeOverrides={ONBOARDING_PICKER_THEME_OVERRIDES}
                  forceDarkAppearance
                />
              </View>

              {/* Industry */}
              <View style={styles.fieldContainer}>
                <ThemedText style={styles.label}> 
                  Industry <ThemedText style={styles.required}>*</ThemedText>
                </ThemedText>
                <CustomPicker
                  selectedValue={formData.industry || undefined}
                  onValueChange={value => handleInputChange('industry', value || '')}
                  items={[
                    { label: 'Select your industry', value: undefined },
                    ...INDUSTRIES.map(industry => ({
                      label: industry,
                      value: industry,
                    })),
                  ]}
                  placeholder="Select your industry"
                  searchable
                  searchPlaceholder="Search industries"
                  themeOverrides={ONBOARDING_PICKER_THEME_OVERRIDES}
                  forceDarkAppearance
                />
              </View>

              {/* Country */}
              <View style={styles.fieldContainer}>
                <ThemedText style={styles.label}> 
                  Country <ThemedText style={styles.required}>*</ThemedText>
                </ThemedText>
                <CustomPicker
                  selectedValue={formData.country || undefined}
                  onValueChange={value => handleInputChange('country', value || '')}
                  items={[
                    { label: 'Select your country', value: undefined },
                    ...getCountries().map(country => ({
                      label: country,
                      value: country,
                    })),
                  ]}
                  placeholder="Select your country"
                  searchable
                  searchPlaceholder="Search countries"
                  themeOverrides={ONBOARDING_PICKER_THEME_OVERRIDES}
                  forceDarkAppearance
                />
              </View>

              {/* City */}
              <View style={styles.fieldContainer}>
                <ThemedText style={styles.label}> 
                  City <ThemedText style={styles.required}>*</ThemedText>
                </ThemedText>
                <CustomPicker
                  selectedValue={formData.city || undefined}
                  onValueChange={value => handleInputChange('city', value || '')}
                  items={[
                    { label: formData.country ? 'Select your city' : 'Select country first', value: undefined },
                    ...availableCities.map(city => ({
                      label: city,
                      value: city,
                    })),
                  ]}
                  placeholder={formData.country ? 'Select your city' : 'Select country first'}
                  searchable
                  searchPlaceholder="Search cities"
                  disabled={!formData.country}
                  themeOverrides={ONBOARDING_PICKER_THEME_OVERRIDES}
                  forceDarkAppearance
                />
              </View>

              {/* Gender */}
              <View style={styles.fieldContainer}>
                <ThemedText style={styles.label}> 
                  Gender <ThemedText style={styles.required}>*</ThemedText>
                </ThemedText>
                <CustomPicker
                  selectedValue={formData.gender || undefined}
                  onValueChange={value => handleInputChange('gender', value || '')}
                  items={[
                    { label: 'Select your gender', value: undefined },
                    ...GENDERS.map(gender => ({
                      label: gender,
                      value: gender,
                    })),
                  ]}
                  placeholder="Select your gender"
                  themeOverrides={ONBOARDING_PICKER_THEME_OVERRIDES}
                  forceDarkAppearance
                />
              </View>
            </View>
            
            {/* Continue Button - at end of form */}
            <View style={styles.buttonContainer}>
              <TouchableOpacity
                style={[
                  styles.button,
                  { backgroundColor: buttonBackgroundColor },
                ]}
                onPress={handleSubmit}
                disabled={isButtonDisabled}
              >
                {loading ? (
                  <ActivityIndicator color={buttonTextColor} />
                ) : (
                  <ThemedText
                    style={[
                      styles.buttonText,
                      { color: buttonTextColor },
                    ]}
                  >
                    Continue
                  </ThemedText>
                )}
              </TouchableOpacity>
            </View>
            
            {/* Bottom spacer */}
            <View style={styles.bottomSpacer} />
            
            {/* Keyboard spacer - shows when keyboard is open */}
            {isKeyboardVisible && <View style={styles.keyboardSpacer} />}
          </ScrollView>
        </KeyboardAvoidingView>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000000',
  },
  keyboardView: {
    flex: 1,
  },
  contentWrapper: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 24,
    paddingTop: 16,
  },
  header: {
    marginTop: 40,
    marginBottom: 32,
  },
  title: {
    fontSize: 28,
    fontWeight: '600',
    marginBottom: 8,
    color: '#FFFFFF',
  },
  subtitle: {
    fontSize: 16,
    color: '#9CA3AF',
  },
  form: {
    gap: 24,
  },
  photoContainer: {
    alignItems: 'center',
    marginBottom: 8,
  },
  photoButton: {
    width: 120,
    height: 120,
    borderRadius: 60,
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: 'rgba(255, 255, 255, 0.3)',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  photoImage: {
    width: '100%',
    height: '100%',
    borderRadius: 60,
  },
  photoPlaceholder: {
    alignItems: 'center',
    gap: 4,
  },
  photoText: {
    fontSize: 12,
    marginTop: 4,
    color: '#6B7280',
  },
  photoLabel: {
    fontSize: 12,
    marginTop: 8,
    color: '#9CA3AF',
  },
  fieldContainer: {
    marginBottom: 4,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 8,
    color: '#FFFFFF',
  },
  required: {
    color: '#D32F2F',
  },
  input: {
    height: 56,
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 16,
    fontSize: 18,
    borderColor: 'rgba(255, 255, 255, 0.2)',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    color: '#FFFFFF',
  },
  characterCount: {
    fontSize: 12,
    textAlign: 'right',
    marginTop: 4,
    color: '#6B7280',
  },
  pickerContainer: {
    borderWidth: 1,
    borderRadius: 8,
    overflow: 'hidden',
  },
  picker: {
    height: 56,
  },
  buttonContainer: {
    marginTop: 32,
    marginBottom: 8,
  },
  button: {
    height: 56,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  buttonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  bottomSpacer: {
    height: 40,
  },
  keyboardSpacer: {
    height: 300,
  },
});

export default OnboardingScreen;
